import { Link } from "react-router-dom";
import { Seo } from "../components/Seo";
import { ArrowUpRight } from "lucide-react";
import { PA_BASE, IMAGES, img } from "../config";
import { KineticHero } from "../components/KineticHero";
import { Reveal, Overline } from "../components/motion";
import { DESTINATIONS } from "../data/destinations";
import { ROUTES } from "../data/routes";

export default function DestinationsPage() {
  return (
    <>
      <Seo
        title="Private Jet Destinations — Dubai, Delhi, Mumbai, Maldives, London, Singapore | JS Private Aviation"
        description="Private charter to Dubai, Delhi, Mumbai, the Maldives, London and Singapore — and anywhere else with an airfield. Arranged through independent operators."
      />
      <KineticHero
        testId="destinations-hero"
        fullHeight={false}
        overline="Destinations"
        lines={["Where you need", "to be."]}
        sub="A selection of the destinations we arrange most often. Private charter reaches far beyond this list — if there is an airfield, we can usually get you there."
        image={img(IMAGES.dubai, 2000)}
      />

      <section className="bg-[#F9F9F6] py-20 sm:py-28">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <Reveal>
            <Overline>City guides</Overline>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight">Destinations, privately.</h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {DESTINATIONS.map((d, i) => (
              <Reveal key={d.slug} delay={0.05 * (i % 3)}>
                <Link
                  to={`${PA_BASE}/destinations/${d.slug}`}
                  data-testid={`destination-card-${d.slug}`}
                  className="group relative block overflow-hidden bg-black"
                >
                  <img
                    src={d.image}
                    alt={d.name}
                    loading="lazy"
                    className="aspect-[3/4] w-full object-cover opacity-90 transition-all duration-700 group-hover:scale-[1.04] group-hover:opacity-75"
                  />
                  <div className="absolute inset-0 bg-black/25" />
                  <div className="absolute inset-x-0 bottom-0 p-7 text-white">
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60">{d.country}</p>
                    <div className="mt-1 flex items-center justify-between">
                      <h3 className="font-display text-3xl tracking-tight">{d.name}</h3>
                      <ArrowUpRight
                        size={20}
                        className="text-white/50 transition-all duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-white"
                      />
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#0A0A0A] py-24 sm:py-32 text-white">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <Reveal>
            <Overline dark>Route guides</Overline>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight">Frequently arranged routes.</h2>
          </Reveal>
          <div className="mt-12">
            {ROUTES.map((r, i) => (
              <Reveal key={r.slug} delay={0.04 * i}>
                <Link
                  to={`${PA_BASE}/routes/${r.slug}`}
                  data-testid={`destinations-route-${r.slug}`}
                  className="group flex items-center justify-between border-t border-white/10 py-6 last:border-b transition-colors duration-300 hover:bg-white/[0.03]"
                >
                  <span className="px-2 font-display text-xl sm:text-2xl tracking-tight">
                    {r.from} <span className="text-white/35">→</span> {r.to}
                  </span>
                  <ArrowUpRight
                    size={18}
                    className="text-white/30 transition-all duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-white"
                  />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
